import React, { useState, useEffect } from 'react';
import { Card, Form, Select, Button, Space, Table, Tag, Modal, message, Spin, Tabs } from 'antd';
import { SaveOutlined, PlusOutlined, EditOutlined, DeleteOutlined, DollarOutlined, PercentageOutlined, UserOutlined } from '@ant-design/icons';
import { TYPO3Service } from '../../../services/typo3Service';
import { useUpdate } from '@refinedev/core';

const { Option } = Select;
const { TabPane } = Tabs;

interface IPriceProfile {
  uid: number;
  name: string;
  description?: string;
  start_date_pricing?: string;
  end_date_pricing?: string;
  price: any;
  status: boolean;
}

interface IAdditionalPrice {
  uid: number;
  title: string;
  price: number;
  description: string;
  optional: boolean;
  vat: number;
}

interface IPricePeriod {
  uid: number;
  title: string;
  start_date_pricing: string;
  end_date_pricing: string;
  price: any;
  priority: number;
}

interface ITaxSetting {
  uid: number;
  title: string;
  type: string;
  start_date_pricing: string;
  end_date_pricing: string;
  price: any;
}

interface PricingTabProps {
  advertId: string;
  advertData: any;
}

export const PricingTab: React.FC<PricingTabProps> = ({ advertId, advertData }) => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const { mutate: updateAdvert } = useUpdate();

  // Pricing profiles states
  const [standardPrices, setStandardPrices] = useState<IPriceProfile[]>([]);
  const [additionalPrices, setAdditionalPrices] = useState<IAdditionalPrice[]>([]);
  const [pricePeriods, setPricePeriods] = useState<IPricePeriod[]>([]);
  const [taxSettings, setTaxSettings] = useState<ITaxSetting[]>([]);

  // Selected profiles
  const [selectedStandardPrice, setSelectedStandardPrice] = useState<number | null>(null);
  const [selectedAdditionalPrices, setSelectedAdditionalPrices] = useState<number[]>([]);
  const [selectedPricePeriods, setSelectedPricePeriods] = useState<number[]>([]);
  const [selectedTaxes, setSelectedTaxes] = useState<number[]>([]);

  useEffect(() => {
    loadPricingData();
  }, []);

  const loadPricingData = async () => {
    setLoading(true);
    try {
      // Load all pricing profiles for the current user
      const [standard, additional, periods, taxes] = await Promise.all([
        loadStandardPrices(),
        loadAdditionalPrices(),
        loadPricePeriods(),
        loadTaxSettings()
      ]);

      setStandardPrices(standard);
      setAdditionalPrices(additional);
      setPricePeriods(periods);
      setTaxSettings(taxes);

      // Set current selections from advertData
      if (advertData) {
        // Try to find which standard price matches the current price
        if (advertData.price && standard.length > 0) {
          const currentPriceStr = typeof advertData.price === 'string' ? advertData.price : JSON.stringify(advertData.price);
          const matchingStandardPrice = standard.find(sp => {
            const spPriceStr = typeof sp.price === 'string' ? sp.price : JSON.stringify(sp.price);
            return spPriceStr === currentPriceStr;
          });
          if (matchingStandardPrice) {
            setSelectedStandardPrice(matchingStandardPrice.uid);
          }
        }
        
        // Set selections from JSON fields
        if (advertData.active_spezialprices_json) {
          setSelectedAdditionalPrices([advertData.active_spezialprices_json]);
        }
        
        if (advertData.priceperiods_json) {
          try {
            const periods = JSON.parse(advertData.priceperiods_json);
            setSelectedPricePeriods(periods.map((p: any) => p.uid));
          } catch (e) {
            console.error('Error parsing priceperiods_json:', e);
          }
        }
        
        if (advertData.taxes_json) {
          try {
            const taxes = JSON.parse(advertData.taxes_json);
            setSelectedTaxes(taxes.map((t: any) => t.uid));
          } catch (e) {
            console.error('Error parsing taxes_json:', e);
          }
        }
      }
    } catch (error) {
      console.error('Error loading pricing data:', error);
      message.error('Fehler beim Laden der Preisdaten');
    } finally {
      setLoading(false);
    }
  };

  const loadStandardPrices = async (): Promise<IPriceProfile[]> => {
    try {
      const apiUrl = `${window.location.origin}/?type=8847379247&tx_buchungsmanager_frontend[controller]=RestApi&tx_buchungsmanager_frontend[action]=getStandardPrices`;
      const response = await fetch(apiUrl, {
        credentials: 'include',
        headers: { 'Accept': 'application/json' }
      });
      const data = await response.json();
      // Map the response to ensure consistent field names
      if (data.standardPrices && Array.isArray(data.standardPrices)) {
        return data.standardPrices.map((sp: any) => ({
          ...sp,
          price: typeof sp.price === 'string' ? JSON.parse(sp.price) : sp.price
        }));
      }
      return [];
    } catch (error) {
      console.error('Error loading standard prices:', error);
      return [];
    }
  };

  const loadAdditionalPrices = async (): Promise<IAdditionalPrice[]> => {
    try {
      const apiUrl = `${window.location.origin}/?type=8847379247&tx_buchungsmanager_frontend[controller]=RestApi&tx_buchungsmanager_frontend[action]=getAdditionalPrices`;
      const response = await fetch(apiUrl, {
        credentials: 'include',
        headers: { 'Accept': 'application/json' }
      });
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      console.error('Error loading additional prices:', error);
      return [];
    }
  };

  const loadPricePeriods = async (): Promise<IPricePeriod[]> => {
    try {
      const apiUrl = `${window.location.origin}/?type=8847379247&tx_buchungsmanager_frontend[controller]=RestApi&tx_buchungsmanager_frontend[action]=getPricePeriods`;
      const response = await fetch(apiUrl, {
        credentials: 'include',
        headers: { 'Accept': 'application/json' }
      });
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      console.error('Error loading price periods:', error);
      return [];
    }
  };

  const loadTaxSettings = async (): Promise<ITaxSetting[]> => {
    try {
      const apiUrl = `${window.location.origin}/?type=8847379247&tx_buchungsmanager_frontend[controller]=RestApi&tx_buchungsmanager_frontend[action]=getTaxSettings`;
      const response = await fetch(apiUrl, {
        credentials: 'include',
        headers: { 'Accept': 'application/json' }
      });
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      console.error('Error loading tax settings:', error);
      return [];
    }
  };

  const handleSave = async () => {
    try {
      const selectedStandardPriceData = standardPrices.find(p => p.uid === selectedStandardPrice);
      const selectedAdditionalPricesData = additionalPrices.filter(p => selectedAdditionalPrices.includes(p.uid));
      const selectedPricePeriodsData = pricePeriods.filter(p => selectedPricePeriods.includes(p.uid));
      const selectedTaxesData = taxSettings.filter(t => selectedTaxes.includes(t.uid));

      const updateData = {
        // Standard price goes into the main price field
        price: selectedStandardPriceData ? JSON.stringify(selectedStandardPriceData.price) : '',
        
        // Special prices - store selected additional prices
        spezialpricesJson: JSON.stringify(selectedAdditionalPricesData),
        activeSpezialpricesJson: selectedAdditionalPrices.length > 0 ? selectedAdditionalPrices[0] : 0,
        
        // Price periods
        priceperiodsJson: JSON.stringify(selectedPricePeriodsData),
        activePriceperiodsJson: selectedPricePeriods.length > 0 ? selectedPricePeriods[0] : 0,
        
        // Taxes
        taxesJson: JSON.stringify(selectedTaxesData),
        activeTaxes: selectedTaxes.length > 0 ? selectedTaxes[0] : 0,
      };

             // API-Aufruf zum Speichern der Preiseinstellungen
       const apiUrl = `${window.location.origin}/?type=8847379247&tx_buchungsmanager_frontend[controller]=RestApi&tx_buchungsmanager_frontend[action]=updateAdvertPricing&tx_buchungsmanager_frontend[id]=${advertId}`;
       
       const response = await fetch(apiUrl, {
         method: 'POST',
         credentials: 'include',
         headers: {
           'Content-Type': 'application/json',
           'Accept': 'application/json',
         },
         body: JSON.stringify(updateData)
       });
       
       if (response.ok) {
         message.success('Preiseinstellungen erfolgreich gespeichert');
       } else {
         const errorData = await response.json();
         console.error('Error saving pricing settings:', errorData);
         message.error('Fehler beim Speichern der Preiseinstellungen');
       }
    } catch (error) {
      console.error('Error in handleSave:', error);
      message.error('Fehler beim Speichern');
    }
  };

  const standardPriceColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Preis (1-2 Pers.)',
      key: 'price_1_2',
      render: (_: any, record: any) => {
        const price = typeof record.price === 'string' ? JSON.parse(record.price) : record.price;
        return (
          <Space>
            <span>{price?.forOnePerson || 0}€</span>
            <span>/</span>
            <span>{price?.forTwoPersons || 0}€</span>
          </Space>
        );
      },
    },
    {
      title: 'Pro weitere',
      key: 'price_additional',
      render: (_: any, record: any) => {
        const price = typeof record.price === 'string' ? JSON.parse(record.price) : record.price;
        return `${price?.perAdditionalPerson || 0}€`;
      },
    },
    {
      title: 'Gültig von',
      dataIndex: 'start_date_pricing',
      key: 'start_date_pricing',
      render: (date: string) => date ? new Date(date).toLocaleDateString('de-DE') : '-',
    },
    {
      title: 'Gültig bis',
      dataIndex: 'end_date_pricing',
      key: 'end_date_pricing',
      render: (date: string) => date ? new Date(date).toLocaleDateString('de-DE') : '-',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: boolean) => (
        <Tag color={status ? 'green' : 'red'}>
          {status ? 'Aktiv' : 'Inaktiv'}
        </Tag>
      ),
    },
  ];

  const additionalPriceColumns = [
    {
      title: 'Bezeichnung',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Preis',
      dataIndex: 'price',
      key: 'price',
      render: (price: number) => `${price || 0}€`,
    },
    {
      title: 'MwSt.',
      dataIndex: 'vat',
      key: 'vat',
      render: (vat: number) => `${vat || 0}%`,
    },
    {
      title: 'Optional',
      dataIndex: 'optional',
      key: 'optional',
      render: (optional: boolean) => (
        <Tag color={optional ? 'blue' : 'default'}>
          {optional ? 'Optional' : 'Pflicht'}
        </Tag>
      ),
    },
  ];

  const pricePeriodColumns = [
    {
      title: 'Bezeichnung',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Preis (1-2 Pers.)',
      key: 'price_1_2',
      render: (_: any, record: any) => {
        const price = typeof record.price === 'string' ? JSON.parse(record.price) : record.price;
        return (
          <Space>
            <span>{price?.forOnePerson || 0}€</span>
            <span>/</span>
            <span>{price?.forTwoPersons || 0}€</span>
          </Space>
        );
      },
    },
    {
      title: 'Gültig von',
      dataIndex: 'start_date_pricing',
      key: 'start_date_pricing',
      render: (date: string) => date ? new Date(date).toLocaleDateString('de-DE') : '-',
    },
    {
      title: 'Gültig bis',
      dataIndex: 'end_date_pricing',
      key: 'end_date_pricing',
      render: (date: string) => date ? new Date(date).toLocaleDateString('de-DE') : '-',
    },
    {
      title: 'Priorität',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority: number) => (
        <Tag color={priority > 5 ? 'red' : priority > 0 ? 'orange' : 'default'}>
          {priority || 0}
        </Tag>
      ),
    },
  ];

  const taxSettingColumns = [
    {
      title: 'Bezeichnung',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Typ',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => {
        let label = 'Pauschal';
        let icon = <DollarOutlined />;
        if (type === 'percent') {
          label = 'Prozentual';
          icon = <PercentageOutlined />;
        } else if (type === 'perperson') {
          label = 'Pro Person';
          icon = <UserOutlined />;
        }
        return <Tag icon={icon}>{label}</Tag>;
      },
    },
    {
      title: 'Wert',
      key: 'value',
      render: (_: any, record: any) => {
        const price = typeof record.price === 'string' ? JSON.parse(record.price) : record.price;
        if (record.type === 'percent') {
          return `${price?.pauschal || 0}%`;
        } else if (record.type === 'perperson') {
          return `${price?.forOnePerson || 0}€ pro Person`;
        } else {
          return `${price?.pauschal || 0}€`;
        }
      },
    },
    {
      title: 'Gültig von',
      dataIndex: 'start_date_pricing',
      key: 'start_date_pricing',
      render: (date: string) => date ? new Date(date).toLocaleDateString('de-DE') : '-',
    },
    {
      title: 'Gültig bis',
      dataIndex: 'end_date_pricing',
      key: 'end_date_pricing',
      render: (date: string) => date ? new Date(date).toLocaleDateString('de-DE') : '-',
    },
  ];

  if (loading) {
    return (
      <Card>
        <Spin size="large" />
      </Card>
    );
  }

  return (
    <Card title="Preise & Steuern" extra={
      <Button type="primary" icon={<SaveOutlined />} onClick={handleSave}>
        Speichern
      </Button>
    }>
      <Tabs defaultActiveKey="1">
        <TabPane tab="Standardpreise" key="1">
          <Form form={form} layout="vertical">
            <Form.Item label="Standardpreis-Profil auswählen">
              <Select
                value={selectedStandardPrice}
                onChange={setSelectedStandardPrice}
                placeholder="Wählen Sie ein Preisprofil"
                style={{ width: '100%' }}
                allowClear
              >
                {standardPrices.filter(p => p.status).map(price => (
                  <Option key={price.uid} value={price.uid}>
                    {price.name} {price.start_date_pricing && price.end_date_pricing && 
                      `(${new Date(price.start_date_pricing).toLocaleDateString('de-DE')} - ${new Date(price.end_date_pricing).toLocaleDateString('de-DE')})`}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            
            {selectedStandardPrice && (
              <Table
                dataSource={standardPrices.filter(p => p.uid === selectedStandardPrice)}
                columns={standardPriceColumns}
                pagination={false}
                rowKey="uid"
                size="small"
              />
            )}
          </Form>
        </TabPane>

        <TabPane tab="Zeitperioden-Preise" key="2">
          <Form.Item label="Preis-Zeitperioden auswählen">
            <Select
              mode="multiple"
              value={selectedPricePeriods}
              onChange={setSelectedPricePeriods}
              placeholder="Wählen Sie Preis-Zeitperioden"
              style={{ width: '100%' }}
            >
              {pricePeriods.map(period => (
                <Option key={period.uid} value={period.uid}>
                  {period.title} ({new Date(period.start_date_pricing).toLocaleDateString('de-DE')} - {new Date(period.end_date_pricing).toLocaleDateString('de-DE')})
                </Option>
              ))}
            </Select>
          </Form.Item>
          
          {selectedPricePeriods.length > 0 && (
            <Table
              dataSource={pricePeriods.filter(p => selectedPricePeriods.includes(p.uid))}
              columns={pricePeriodColumns}
              pagination={false}
              rowKey="uid"
              size="small"
              style={{ marginTop: 16 }}
            />
          )}
        </TabPane>

        <TabPane tab="Zusatzleistungen" key="3">
          <Form.Item label="Zusatzleistungen auswählen">
            <Select
              mode="multiple"
              value={selectedAdditionalPrices}
              onChange={setSelectedAdditionalPrices}
              placeholder="Wählen Sie Zusatzleistungen"
              style={{ width: '100%' }}
            >
              {additionalPrices.map(price => (
                <Option key={price.uid} value={price.uid}>
                  {price.title} - {price.price}€ ({price.vat}% MwSt.)
                </Option>
              ))}
            </Select>
          </Form.Item>
          
          {selectedAdditionalPrices.length > 0 && (
            <Table
              dataSource={additionalPrices.filter(p => selectedAdditionalPrices.includes(p.uid))}
              columns={additionalPriceColumns}
              pagination={false}
              rowKey="uid"
              size="small"
              style={{ marginTop: 16 }}
            />
          )}
        </TabPane>

        <TabPane tab="Steuern & Gebühren" key="4">
          <Form.Item label="Steuern & Gebühren auswählen">
            <Select
              mode="multiple"
              value={selectedTaxes}
              onChange={setSelectedTaxes}
              placeholder="Wählen Sie Steuern & Gebühren"
              style={{ width: '100%' }}
            >
              {taxSettings.map(tax => (
                <Option key={tax.uid} value={tax.uid}>
                  {tax.title} ({tax.type})
                </Option>
              ))}
            </Select>
          </Form.Item>
          
          {selectedTaxes.length > 0 && (
            <Table
              dataSource={taxSettings.filter(t => selectedTaxes.includes(t.uid))}
              columns={taxSettingColumns}
              pagination={false}
              rowKey="uid"
              size="small"
              style={{ marginTop: 16 }}
            />
          )}
        </TabPane>
      </Tabs>
      
      {/* Zusammenfassung der gewählten Preise */}
      {(selectedStandardPrice || selectedPricePeriods.length > 0 || selectedAdditionalPrices.length > 0 || selectedTaxes.length > 0) && (
        <Card title="Preisübersicht" style={{ marginTop: 16 }} size="small">
          <Space direction="vertical" style={{ width: '100%' }}>
            {selectedStandardPrice && (
              <div>
                <strong>Standardpreis:</strong> {standardPrices.find(p => p.uid === selectedStandardPrice)?.name}
              </div>
            )}
            {selectedPricePeriods.length > 0 && (
              <div>
                <strong>Zeitperioden:</strong> {pricePeriods.filter(p => selectedPricePeriods.includes(p.uid)).map(p => p.title).join(', ')}
              </div>
            )}
            {selectedAdditionalPrices.length > 0 && (
              <div>
                <strong>Zusatzleistungen:</strong> {additionalPrices.filter(p => selectedAdditionalPrices.includes(p.uid)).map(p => p.title).join(', ')}
              </div>
            )}
            {selectedTaxes.length > 0 && (
              <div>
                <strong>Steuern & Gebühren:</strong> {taxSettings.filter(t => selectedTaxes.includes(t.uid)).map(t => t.title).join(', ')}
              </div>
            )}
          </Space>
        </Card>
      )}
    </Card>
  );
}; 